import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, IncomeTaxEntity, Onderneming} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiAlertModule} from "@snelstart/ui-alert";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-omzetbelasting-passiva',
  templateUrl: './spec-omzetbelasting-passiva.component.html',
  imports: [
    SharedUiDialogModule,
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule,
    SharedUiAlertModule
  ],
  styleUrls: ['./spec-omzetbelasting-passiva.component.scss']
})
export class SpecOmzetbelastingPassivaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public dialogVisible = false;
  public isPartner = false;

  public formGroup = new FormGroup({
    waardeOpening: new FormControl<number | null>(null),
    waardeSlot: new FormControl<number | null>(null),
    nominaleWaarde: new FormControl<number | null>(null),
    omzetbelastingDitBoekjaar: new FormControl<number | null>(null),
    omzetbelastingVorigBoekjaar: new FormControl<number | null>(null),
    omzetbelastingOudereBoekjaren: new FormControl<number | null>(null),
    omschrijving: new FormControl<string | null>('omzetbelastingSchuld'),
  })

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  showDialog() {
    this.handleSwitch(this.dataService.incomeTaxEntity.value, this.isPartner);
    this.dialogVisible = true;
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroup();
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private resetFormGroup() {
    this.formGroup.setValue({
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number,
      nominaleWaarde: null as unknown as number,
      omzetbelastingDitBoekjaar: null as unknown as number,
      omzetbelastingVorigBoekjaar: null as unknown as number,
      omzetbelastingOudereBoekjaren: null as unknown as number,
      omschrijving: 'omzetbelastingSchuld'
    }, {emitEvent: false})
  }

  private patchFormGroups(ondernemingen: Onderneming[]) {
    this.resetFormGroup();
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    if (onderneming.kortlopendeSchulden?.at(1)) {
      // omzetbelasting staat altijd op positie 1 in de json
      this.formGroup.patchValue(onderneming.kortlopendeSchulden[1], {emitEvent: false});
    }
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        // Clone or create the array
        const kortlopendeSchulden = Array.isArray(onderneming.kortlopendeSchulden)
          ? [...onderneming.kortlopendeSchulden]
          : [];
        // Ensure the array has at least  items
        while (kortlopendeSchulden.length < 2) {
          kortlopendeSchulden.push({});
        }
        // Update the second item
        kortlopendeSchulden[1] = formValue;

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          kortlopendeSchulden,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }

  getTotaalSchuldSpec(): number {
    const omzetbelastingDitBoekjaar = this.formGroup.get('omzetbelastingDitBoekjaar')?.value ?? 0
    const omzetbelastingVorigBoekjaar = this.formGroup.get('omzetbelastingVorigBoekjaar')?.value ?? 0
    const omzetbelastingOudereBoekjaren = this.formGroup.get('omzetbelastingOudereBoekjaren')?.value ?? 0
    return omzetbelastingDitBoekjaar + omzetbelastingVorigBoekjaar + omzetbelastingOudereBoekjaren;
  }
}
